/**
 * Paths inside the manual (`docs/manual`). A page is named by its file without the extension
 * (`primeros-pasos`, `referencia/plataforma`, `README` for the index), and everything a page links
 * to (another page, a screenshot) is written relative to the page's own file.
 */

/** The page every other one hangs from: the manual's own index. */
export const INDEX_PAGE = 'README';

/** `referencia/plataforma.md` -> `referencia/plataforma`. */
export function pageKeyOf(file: string): string {
  return file.replace(/\.md$/, '');
}

/**
 * Resolves `target` against the folder of `fromFile`. Returns null when the result climbs out of the
 * manual's folder, so a link cannot point at anything else.
 */
export function resolveManualPath(fromFile: string, target: string): string | null {
  const folder = fromFile.split('/').slice(0, -1);
  const parts = target.startsWith('/') ? [] : folder;
  const resolved: string[] = [...parts];

  for (const part of target.split('/')) {
    if (part === '' || part === '.') {
      continue;
    }
    if (part === '..') {
      if (resolved.length === 0) {
        return null;
      }
      resolved.pop();
    } else {
      resolved.push(part);
    }
  }
  return resolved.join('/');
}

export type ManualLink =
  | { kind: 'page'; page: string; anchor: string | undefined }
  | { kind: 'anchor'; anchor: string }
  | { kind: 'external'; href: string }
  | { kind: 'unknown' };

/** Anchors travel percent-encoded when an address is copied; the headings' ids are plain. */
export function decodeAnchor(anchor: string): string {
  try {
    return decodeURIComponent(anchor);
  } catch {
    return anchor;
  }
}

/** What a link written in `fromFile` points at. */
export function classifyLink(fromFile: string, href: string): ManualLink {
  if (/^(https?:|mailto:)/i.test(href)) {
    return { kind: 'external', href };
  }
  if (href.startsWith('#')) {
    return href.length > 1
      ? { kind: 'anchor', anchor: decodeAnchor(href.slice(1)) }
      : { kind: 'unknown' };
  }

  const hashAt = href.indexOf('#');
  const path = hashAt === -1 ? href : href.slice(0, hashAt);
  const anchor =
    hashAt === -1 || hashAt === href.length - 1 ? undefined : decodeAnchor(href.slice(hashAt + 1));
  if (!path.endsWith('.md')) {
    return { kind: 'unknown' };
  }
  const resolved = resolveManualPath(fromFile, path);
  return resolved === null
    ? { kind: 'unknown' }
    : { kind: 'page', page: pageKeyOf(resolved), anchor };
}
